/**
 * 384, the catch-up pace (fix/384-catch-up-rate): how many ticks a wall-time host's frame may run (Match.advance's
 * `catchUp`), from its frames' smoothed need. Host pacing only — never tick state: it chooses when a tick runs, never what
 * it does.
 *
 * A frame that missed vsync owes the next more ticks than its share (3–6 at 60 fps on gpuCheck's mega), which make that one
 * long in turn: half mega's slowest 5 % of frames were these. The first cut capped every frame at 2 ticks at 1×, which held
 * the spiral but tied the clock to 60 fps (a 30 fps page played at half speed; the smoke's action/input at load 30 went
 * red). Now a frame runs at most its smoothed need plus HEADROOM (rounded up), and the rest stays owed — up to OWE_TICKS,
 * paid off by the headroom over the next frames; only beyond it is time dropped. So any steady frame rate keeps wall time,
 * and a missed frame's ticks spread over the frames after it instead of landing in one.
 */
import { PHYS_DT } from '../constants';

export const CATCH_UP = {
  /** off: no cap (the accumulator as before 384) */
  on: true,
  /** ticks a frame above the smoothed need, at least (the debt's pay-off rate; the cap is ⌈need + headroom⌉) */
  headroom: 0.5,
  /** ticks left owed after a capped frame, at most (33 ms at 120 Hz); time owed beyond it is dropped */
  oweTicks: 4,
  /** the smoothing of the need, a share a frame (an exponential mean: ~10 frames) */
  smoothing: 0.1,
} as const;

export class CatchUpPace {
  private need: number | null = null;
  constructor(private readonly c: { on: boolean; headroom: number; oweTicks: number; smoothing: number } = CATCH_UP) {}

  /** This frame's cap in ticks for `dt` seconds of (scaled) time, and the ticks it may leave owed; a cap of 0 is no cap. */
  frame(dt: number): { catchUp: number; owe: number } {
    if (!this.c.on) return { catchUp: 0, owe: 0 };
    const n = dt / PHYS_DT;
    this.need = this.need === null ? n : this.need + this.c.smoothing * (n - this.need);
    return { catchUp: Math.max(1, Math.ceil(this.need + this.c.headroom)), owe: this.c.oweTicks };
  }
}
